// src/features/system/devices/components/iot/IoTProvisioningPanel.tsx
// ────────────────────────────────────────────────────────────────────────────
// Step-by-step provisioning guide for onboarding a new ESP32 IoT device.
// This is a read-only reference panel that mirrors the serial CLI workflow.
// ────────────────────────────────────────────────────────────────────────────
import React from 'react';

const STEPS = [
  {
    title: '1. Flash the firmware',
    detail: 'Connect the ESP32 via USB-C and flash using PlatformIO:',
    code: 'pio run --target upload',
  },
  {
    title: '2. Open the serial monitor',
    detail: 'Open a serial terminal at 115 200 baud:',
    code: 'pio device monitor',
  },
  {
    title: '3. Get the hardware identity',
    detail: 'Run the `id` command to retrieve the HW-ID (eFuse MAC) and chip info:',
    code: '> id',
  },
  {
    title: '4. Set the device ID',
    detail: 'Assign a logical device ID that matches the name used in PACMaster:',
    code: '> device-id set plant-monitor-01\n> config save',
  },
  {
    title: '5. Configure WiFi',
    detail: 'Set the WiFi credentials and enable:',
    code: '> wifi set-ssid MyNetwork\n> wifi set-pass MyPassword\n> config save\n> wifi connect',
  },
  {
    title: '6. Set the telemetry endpoint',
    detail: 'Point the device to the PACMaster BFF ingest URL:',
    code: '> endpoint set https://<your-domain>/bff/iot/ingest\n> config save',
  },
  {
    title: '7. Set the publish interval',
    detail: 'Configure how often readings are POSTed (in milliseconds):',
    code: '> period set 60000\n> config save',
  },
  {
    title: '8. Register the device in PACMaster',
    detail:
      'Go to the Device Registry tab and click "+ Register Device".  ' +
      'Enter the device_id and HW-ID from step 3.',
  },
  {
    title: '9. Verify connectivity',
    detail: 'Check the WiFi status and confirm the device appears online in the Dashboard:',
    code: '> wifi status\n> status',
  },
] as const;

export default function IoTProvisioningPanel() {
  return (
    <div className="h-full overflow-auto pr-2">
      <div className="max-w-2xl space-y-6 pb-8">
        <p className="text-sm text-neutral-400">
          Follow these steps to onboard a new <strong className="text-neutral-200">ESP32-IoT</strong> sensor device to the PACMaster platform.
        </p>

        {STEPS.map((step, i) => (
          <div key={i} className="space-y-1.5">
            <h3 className="text-sm font-semibold text-neutral-200">{step.title}</h3>
            <p className="text-xs text-neutral-400">{step.detail}</p>
            {'code' in step && step.code && (
              <pre className="px-3 py-2 rounded pac-glass text-[12px] text-sky-300 font-mono whitespace-pre-wrap">
                {step.code}
              </pre>
            )}
          </div>
        ))}

        <div className="border-t border-neutral-800 pt-4 space-y-2">
          <h3 className="text-sm font-semibold text-neutral-200">CLI Command Reference</h3>
          <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
            {CLI_COMMANDS.map(([cmd, desc]) => (
              <React.Fragment key={cmd}>
                <code className="text-sky-400 font-mono">{cmd}</code>
                <span className="text-neutral-500">{desc}</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const CLI_COMMANDS: [string, string][] = [
  ['help',               'Show all commands'],
  ['id',                 'Hardware ID, device ID, chip, firmware info'],
  ['fw',                 'Firmware name, version, build timestamp'],
  ['status',             'Uptime, free heap, Wi-Fi state, endpoint, period'],
  ['reboot',             'Restart the MCU'],
  ['factory-reset',      'Erase NVS config and reboot'],
  ['config show',        'Display all persisted configuration'],
  ['config save',        'Write current in-memory config to NVS'],
  ['wifi scan',          'Scan and list visible access points'],
  ['wifi set-ssid <s>',  'Set Wi-Fi SSID (in memory)'],
  ['wifi set-pass <p>',  'Set Wi-Fi password (in memory)'],
  ['wifi connect',       'Connect using configured credentials'],
  ['wifi disconnect',    'Disconnect and disable auto-reconnect'],
  ['wifi status',        'Wi-Fi state, IP, RSSI, MAC'],
  ['wifi clear',         'Erase credentials, save, and disconnect'],
  ['endpoint set <url>', 'Set publish endpoint URL'],
  ['endpoint show',      'Display endpoint URL'],
  ['period set <ms>',    'Set publish interval in milliseconds'],
  ['period show',        'Display publish interval'],
  ['device-id set <v>',  'Set logical device ID'],
  ['device-id show',     'Display logical device ID'],
];
